import { Box, Button, Center, Text, Input, Select, HStack, FormControl, FormLabel, useColorModeValue } from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useUserProfile } from "hooks/useUserProfile";
import { getUserInvoices } from "api/billingPlan";
import { useLoadItems } from "hooks/useLoadItems";
import AutoSizer from "react-virtualized-auto-sizer";
import InfiniteLoader from "react-window-infinite-loader";
import { VariableSizeList as List } from "react-window";
import type { VariableSizeList } from "react-window";
import { InvoiceItem } from "screens/common/components/InvoiceItem/InvoiceItem";
import { TypingIndicator } from "screens/thread/components/cells/components";
import { ItemLoadingIndicator } from "screens/landing/tabs/collections/ItemLoadingIndicator";
import type { Invoice } from "types/billing/UserInvoicesResponse";
import { useButtonProps } from "hooks";

const DEFAULT_HEIGHT = 90;
const PAGE_SIZE = 100;
const REFRESH_INTERVAL = 60000; // 1 minutes

const InvoiceRow = ({
  index,
  invoice,
  setRowHeight,
  style,
}: {
  index: number;
  invoice: Invoice;
  setRowHeight: (index: number, height: number) => void;
  style: React.CSSProperties;
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const borderColor = useColorModeValue("gray.200", "gray.700");

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);

  return (
    <Box style={style} ref={rowRef} borderBottom="1px solid" borderColor={borderColor}>
      <InvoiceItem invoice={invoice} />
    </Box>
  );
};

export const PurchasedInvoices = () => {
  const user = useUserProfile();
  const infiniteLoaderRef = useRef<InfiniteLoader | null>(null);
  const listRef = useRef<VariableSizeList | null>(null);
  const rowHeights = useRef<Record<number, number>>({});
  const hasMountedRef = useRef(false);
  const lastDataHashRef = useRef<string>("");
  const [isPollingRefresh, setIsPollingRefresh] = useState(false);
  const bgColor = useColorModeValue("white", "gray.900");
  const primaryButtonProps = useButtonProps("sm", "primary");

  // Filter states
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const loadItems = async (token: string | null) => {
    if (!user.id) return { data: [], totalCount: 0, nextToken: null };

    const invoices = await getUserInvoices(user.id);

    // Apply filters
    const filteredInvoices = invoices.filter((invoice) => {
      // Status filter
      const hasStatus = !statusFilter || invoice.status === statusFilter;

      // Date filters - compare only dates, not times
      const invoiceDate = new Date(invoice.date);
      const invoiceDateOnly = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate());

      const hasAfterStartDate = !startDate || invoiceDateOnly >= new Date(new Date(startDate).setHours(0, 0, 0, 0));
      const hasBeforeEndDate = !endDate || invoiceDateOnly <= new Date(new Date(endDate).setHours(23, 59, 59, 999));

      return hasStatus && hasAfterStartDate && hasBeforeEndDate;
    });

    const sortedInvoices = filteredInvoices.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const startIndex = token ? parseInt(token) : 0;
    const endIndex = startIndex + PAGE_SIZE;
    const paginatedInvoices = sortedInvoices.slice(startIndex, endIndex);
    const nextPageToken = endIndex < sortedInvoices.length ? endIndex.toString() : null;

    return {
      data: paginatedInvoices,
      totalCount: sortedInvoices.length,
      nextToken: nextPageToken,
    };
  };

  // Function to check if data has changed by comparing a hash of the data
  const checkForDataChanges = useCallback(async () => {
    if (!user.id) return false;

    try {
      const invoices = await getUserInvoices(user.id);

      // Apply the same filters as loadItems
      const filteredInvoices = invoices.filter((invoice) => {
        const hasStatus = !statusFilter || invoice.status === statusFilter;

        const invoiceDate = new Date(invoice.date);
        const invoiceDateOnly = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate());
        const hasAfterStartDate = !startDate || invoiceDateOnly >= new Date(new Date(startDate).setHours(0, 0, 0, 0));
        const hasBeforeEndDate = !endDate || invoiceDateOnly <= new Date(new Date(endDate).setHours(23, 59, 59, 999));

        return hasStatus && hasAfterStartDate && hasBeforeEndDate;
      });

      const sortedInvoices = filteredInvoices.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const dataHash = JSON.stringify(sortedInvoices);

      const hasChanged = dataHash !== lastDataHashRef.current;
      if (hasChanged) {
        lastDataHashRef.current = dataHash;
      }

      return hasChanged;
    } catch (error) {
      console.error("Error checking for data changes:", error);
      return false;
    }
  }, [user.id, statusFilter, startDate, endDate]);

  const {
    loading: isLoading,
    hasNextPage,
    loadMore,
    items,
    totalCount,
    clear,
  } = useLoadItems<Invoice>({
    loadItems,
    onClear: () => {
      if (infiniteLoaderRef.current) {
        infiniteLoaderRef.current.resetloadMoreItemsCache(true);
      }
    },
  });

  const clearAllFilters = useCallback(() => {
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    clear();
  }, [clear]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        clearAllFilters();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [clearAllFilters]);

  const itemCount = totalCount ?? (hasNextPage ? items.length + 1 : items.length);
  const loadMoreItems = isLoading ? () => undefined : loadMore;
  const isItemLoaded = useCallback((index: number) => !hasNextPage || index < items.length, [hasNextPage, items]);

  const setRowHeight = useCallback((index: number, size: number) => {
    listRef.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [index]: size };
  }, []);

  const getRowHeight = useCallback((index: number) => {
    return DEFAULT_HEIGHT;
  }, []);

  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const invoice = items[index];
      if (!invoice) {
        return null;
      }
      return <InvoiceRow index={index} invoice={invoice} setRowHeight={setRowHeight} style={style} />;
    },
    [items, setRowHeight]
  );

  useEffect(() => {
    if (!hasMountedRef.current && !isLoading) {
      loadMore();
      hasMountedRef.current = true;
    }

    // TODO: replace polling with websocket or server-sent events
    const pollingInterval = setInterval(async () => {
      const hasDataChanged = await checkForDataChanges();
      if (hasDataChanged) {
        setIsPollingRefresh(true);
        clear();
        loadMore();
      }
    }, REFRESH_INTERVAL);

    const handleRefresh = () => {
      clear();
      loadMore();
    };
    window.addEventListener("refreshInvoices", handleRefresh);

    return () => {
      clearInterval(pollingInterval);
      window.removeEventListener("refreshInvoices", handleRefresh);
    };
  }, [isLoading, loadMore, clear, checkForDataChanges]);

  useEffect(() => {
    if (hasMountedRef.current) {
      // Reset the data hash when filters change so the next poll will refresh
      lastDataHashRef.current = "";
      clear();
    }
  }, [statusFilter, startDate, endDate, clear]);

  // Reset polling refresh state when loading completes
  useEffect(() => {
    if (!isLoading && isPollingRefresh) {
      setIsPollingRefresh(false);
    }
  }, [isLoading, isPollingRefresh]);

  return (
    <Box height="100%" overflow="hidden" position="relative">
      <Box pb="1rem">
        <HStack spacing={4} px={0} align="flex-end">
          <FormControl>
            <FormLabel fontSize="sm">Status</FormLabel>
            <Select
              placeholder="All statuses"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                clear();
              }}
              size="sm">
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Start Date</FormLabel>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                clear();
              }}
              size="sm"
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">End Date</FormLabel>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                clear();
              }}
              size="sm"
            />
          </FormControl>
          <FormControl width="auto">
            <FormLabel fontSize="sm" visibility="hidden">
              Clear
            </FormLabel>
            <Button {...primaryButtonProps} onClick={clearAllFilters}>
              Clear Filters
            </Button>
          </FormControl>
        </HStack>
      </Box>
      {!isLoading && items.length === 0 && hasMountedRef.current && (
        <Center width="100%" height="54vh">
          <Text fontSize="sm" align="start" color="gray.500" fontWeight="400">
            No invoices found
          </Text>
        </Center>
      )}
      {isLoading && items.length === 0 && !isPollingRefresh && (
        <Center width="100%" height="54vh">
          <TypingIndicator />
        </Center>
      )}
      <Box height="100%" overflow="auto">
        <AutoSizer>
          {({ height, width }) => (
            <InfiniteLoader
              ref={(ref) => (infiniteLoaderRef.current = ref)}
              isItemLoaded={isItemLoaded}
              itemCount={itemCount}
              loadMoreItems={loadMoreItems}>
              {({ onItemsRendered, ref }) => (
                <List
                  ref={(list) => {
                    ref(list);
                    listRef.current = list;
                  }}
                  className="List"
                  height={height}
                  itemCount={itemCount}
                  itemSize={getRowHeight}
                  onItemsRendered={onItemsRendered}
                  width={width}>
                  {Row}
                </List>
              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
      </Box>
      <Box position="absolute" bottom="0" width="100%" zIndex={2} bgColor={bgColor} height="3rem" alignContent="center">
        <ItemLoadingIndicator onClick={loadMoreItems} label="Invoices" currentItemCount={items.length} totalItemCount={totalCount ?? 0} />
      </Box>
    </Box>
  );
};
